import { useState, useEffect } from 'react'
import { ProviderUser } from '@magnetar/admin-api-interfaces'
import { AllowedAction } from '@/data/allowedActions'
import { logger } from '@magnetar/utils'
import {
  PROVIDER_USER_PERMISSION_MODULES,
  PROVIDER_USER_PERMISSION_MODULE_IDS,
  buildProviderPermissionsPayload,
} from './providerUserPermissionModules'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useProviderUserPermissions } from '@/hooks/useProviderUsers'
import { providerUsersService } from '@/services/provider_users_service'

interface ProviderUserPermissionDialogProps {
  providerUser: ProviderUser
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

const actionLabels: Record<AllowedAction, string> = {
  [AllowedAction.C]: '新增',
  [AllowedAction.R]: '讀取',
  [AllowedAction.U]: '更新',
  [AllowedAction.D]: '刪除',
}

// 定義模組（id 必須對齊後端 ProviderUserPermissionModule enum，見 ./providerUserPermissionModules）
const modules = PROVIDER_USER_PERMISSION_MODULES

export function ProviderUserPermissionDialog({
  providerUser,
  open,
  onOpenChange,
  onSuccess,
}: ProviderUserPermissionDialogProps) {
  const { token } = useAuth()
  const [isSaving, setIsSaving] = useState(false)
  const { permissions, isLoading, mutate } = useProviderUserPermissions(
    providerUser?.provider_id || null,
    providerUser?.provider_user_id || null,
  )
  const [selectedActions, setSelectedActions] = useState<Record<string, AllowedAction[]>>(() =>
    // 初始化空的權限設定
    Object.fromEntries(PROVIDER_USER_PERMISSION_MODULE_IDS.map((id) => [id, []])),
  )

  // 初始化選中的權限
  useEffect(() => {
    if (permissions && permissions.length > 0) {
      const initialSelectedActions: Record<string, AllowedAction[]> = Object.fromEntries(
        PROVIDER_USER_PERMISSION_MODULE_IDS.map((id) => [id, []]),
      )

      permissions.forEach((permission) => {
        const moduleId = permission.module as string
        // 將 string[] 轉換為 AllowedAction[]
        initialSelectedActions[moduleId] = permission.allowed_action.map(
          (action) => action as AllowedAction,
        )
      })

      setSelectedActions(initialSelectedActions)
    }
  }, [permissions])

  const handleActionToggle = (moduleId: string, action: AllowedAction) => {
    setSelectedActions((prev) => {
      const current = prev[moduleId] || []
      const updated = current.includes(action)
        ? current.filter((a) => a !== action)
        : [...current, action]
      return {
        ...prev,
        [moduleId]: updated,
      }
    })
  }

  const handleSave = async () => {
    if (!token) return

    setIsSaving(true)
    try {
      const permissionsToUpdate = buildProviderPermissionsPayload(selectedActions)

      const response = await providerUsersService.updateProviderUserPermissions(
        providerUser.provider_id,
        providerUser.provider_user_id,
        permissionsToUpdate,
        token,
      )

      if (response.success) {
        toast.success('權限更新成功')
        mutate() // 更新權限列表
        onSuccess?.()
        onOpenChange(false)
      } else {
        toast.error(response.error?.message || '請稍後再試')
      }
    } catch (error) {
      logger.error('Failed to update permissions:', error)
      toast.error('更新權限時發生錯誤，請稍後再試')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>權限設定 - {providerUser.user?.user_name}</DialogTitle>
          <DialogDescription>
            設定此使用者在 {providerUser.provider?.provider_name}{' '}
            的權限。勾選代表允許該操作，取消勾選代表禁止該操作。
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            {modules.map((module) => (
              <div key={module.id} className="space-y-2">
                <Label className="text-base">{module.name}</Label>
                <div className="flex gap-4">
                  {Object.values(AllowedAction).map((action) => (
                    <div key={action} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${module.id}-${action}`}
                        checked={selectedActions[module.id]?.includes(action) || false}
                        onCheckedChange={() => handleActionToggle(module.id, action)}
                      />
                      <Label htmlFor={`${module.id}-${action}`} className="text-sm font-normal">
                        {actionLabels[action]}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={isLoading || isSaving}>
            {isSaving ? '儲存中...' : '儲存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
